// FlowDraftProvider.tsx - the in-progress send (flow type + config values)
// shared across the Merchant Tasks tab. The contract type control writes the
// flow type; SendPanel reads the draft to post the send.
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { FlowType } from "./types";

export type FlowConfigValues = Record<string, unknown>;

export interface FlowDraft {
  flowType: FlowType | null;
  config: FlowConfigValues;
  /** True after a field edit. Untouched drafts follow a fresh defaults response. */
  edited: boolean;
}

export interface FlowDraftContextValue extends FlowDraft {
  borrowerId: number;
  /** Switching to a different type clears the config; the same type is a no-op. */
  setFlowType: (flowType: FlowType) => void;
  /** Replace every config value (e.g. from a fresh `DraftEvaluation`). */
  setConfig: (config: FlowConfigValues) => void;
  /** Write one config value. */
  setConfigValue: (key: string, value: unknown) => void;
  /** Drop one config value, so the server's default applies again. Not an edit. */
  dropConfigValue: (key: string) => void;
  reset: () => void;
}

const EMPTY_DRAFT: FlowDraft = { flowType: null, config: {}, edited: false };

const FlowDraftContext = createContext<FlowDraftContextValue | null>(null);

interface FlowDraftProviderProps {
  borrowerId: number;
  children: React.ReactNode;
}

const FlowDraftState: React.FC<FlowDraftProviderProps> = ({ borrowerId, children }) => {
  const [draft, setDraft] = useState<FlowDraft>(EMPTY_DRAFT);

  const setFlowType = useCallback((flowType: FlowType) => {
    setDraft((prev) =>
      prev.flowType === flowType ? prev : { flowType, config: {}, edited: false }
    );
  }, []);
  const setConfig = useCallback((config: FlowConfigValues) => {
    setDraft((prev) => ({ ...prev, config }));
  }, []);
  const setConfigValue = useCallback((key: string, value: unknown) => {
    setDraft((prev) => ({
      ...prev,
      edited: true,
      config: { ...prev.config, [key]: value },
    }));
  }, []);
  const dropConfigValue = useCallback((key: string) => {
    setDraft((prev) => {
      if (!(key in prev.config)) return prev;
      const { [key]: _dropped, ...config } = prev.config;
      return { ...prev, config };
    });
  }, []);
  const reset = useCallback(() => setDraft(EMPTY_DRAFT), []);

  const value = useMemo<FlowDraftContextValue>(
    () => ({
      ...draft,
      borrowerId,
      setFlowType,
      setConfig,
      setConfigValue,
      dropConfigValue,
      reset,
    }),
    [draft, borrowerId, setFlowType, setConfig, setConfigValue, dropConfigValue, reset]
  );

  return <FlowDraftContext.Provider value={value}>{children}</FlowDraftContext.Provider>;
};

/** Holds one draft per deal; navigating to another deal starts from empty. */
export const FlowDraftProvider: React.FC<FlowDraftProviderProps> = ({ borrowerId, children }) => (
  <FlowDraftState key={borrowerId} borrowerId={borrowerId}>
    {children}
  </FlowDraftState>
);

export function useFlowDraft(): FlowDraftContextValue {
  const value = useContext(FlowDraftContext);
  if (!value) throw new Error("useFlowDraft must be used inside <FlowDraftProvider>");
  return value;
}
